// External Libs
import React from "react"

// External files
import { LOGIN_URL } from "../../constants/urls"
import { fetchCampaign } from "../../services/dataStore"
import {
  PROMO_CODE_COOKIE,
  COOKIE_DOMAIN,
  COOKIE_PATH,
} from "../../constants/cookies"

// Non Typed External Libs
const Cookies = require("js-cookie")

interface PromoCheckerProps {
  buttonColor?: string,
  buttonTextColor?: string,
  buttonHoverColor?: string,
  buttonActiveColor?: string,
  callToAction?: string,
  codePreFix?: string,
  transform?: boolean,
  finePrint?: string,
  allowLogin?: boolean,
  errorMessage?: string,
  successURL?: string,
}

interface PromoCheckerState {
  promoCode?: string,
  hasError: boolean
}

class PromoCheckerInner extends React.Component<
PromoCheckerProps,
PromoCheckerState> {
  public static defaultProps = {
    callToAction: "Apply Promotion",
    codePreFix: "",
    transform: false,
    finePrint: "",
    allowLogin: true,
    errorMessage: "This is not a valid promotional code, please try again.",
    successURL: "",
    buttonColor: "black",
    buttonTextColor: "white",
    buttonHoverColor: "black-secondary",
    buttonActiveColor: "gray-dark",
  }

  constructor(props = {}) {
    super(props)
    this.state = {
      hasError: false,
    }

    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleChange = this.handleChange.bind(this)
  }

  private savePromoCode(code: string) {
    Cookies.set(
      PROMO_CODE_COOKIE,
      code,
      { domain: COOKIE_DOMAIN, path: COOKIE_PATH },
    )
  }

  private redirectUser(path: string): void {
    window.location.assign(path)
  }

  private validatePromoCode(promoCode: string) {
    const { codePreFix } = this.props
    const pattern = new RegExp(`${codePreFix}.+`, "g")
    return pattern.test(promoCode)
  }

  private transformPromoCode(promoCode: string) {
    const { transform } = this.props

    if (transform) {
      const noHyphens = promoCode.replace(/-/g, "")
      return noHyphens.toUpperCase()
    }
    return promoCode
  }

  private async handleSubmit(e) {
    e.preventDefault()
    let { promoCode } = this.state
    const { successURL } = this.props
    promoCode = this.transformPromoCode(promoCode)

    try {
      const valid = this.validatePromoCode(promoCode)

      if (valid) {
        const query = "code"
        await fetchCampaign(query, promoCode)
        this.savePromoCode(promoCode)
        this.redirectUser(successURL)
      } else {
        throw new Error("Promo code not valid")
      }
    } catch (error) {
      this.setState({ hasError: true })
    }
  }

  handleChange(e) {
    this.setState({ promoCode: e.target.value, hasError: false })
  }

  renderError() {
    const { hasError } = this.state

    if (hasError) {
      const { errorMessage } = this.props

      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          { errorMessage }
        </div>
      )
    }
  }

  renderFinePrint() {
    const { finePrint } = this.props

    if (finePrint) {
      return (
        <p className="pt-4 text-xs text-center">
          { finePrint }
        </p>
      )
    }
  }

  renderLogin() {
    const { allowLogin } = this.props

    if (allowLogin) {
      return (
        <p className="pt-4 text-xs text-center">
          Already a member?
          <a href={LOGIN_URL} className="font-semibold underline mx-1 text-current" style={{ color: "inherit", textDecoration: "underline" }}>Login</a>
        </p>
      )
    }
  }

  render() {
    const {
      callToAction,
      buttonColor,
      buttonTextColor,
      buttonHoverColor,
      buttonActiveColor,
    } = this.props

    return (
      <div className="w-full">
        <form className="form" onSubmit={this.handleSubmit}>
          <div className="mb-4">
            <label className="sr-only" htmlFor="promo_code">Promo Code</label>
            <input
              type="string"
              placeholder="Enter your promo code"
              className="text-base text-black-secondary border border-solid border-gray-400 w-full px-4 py-2 focus:border-primary shadow-inner"
              id="promo_code"
              name="promo_code"
              minLength={3}
              required
              onChange={this.handleChange}
            />
          </div>

          {this.renderError()}

          <button
            type="submit"
            className={`inline-block text-center font-bold rounded-full leading-snug
              no-underline transition-all duration-200 ease-in-out
              active:[box-shadow:0px_4px_4px_0px_rgba(0,0,0,0.25)_inset]
              hover:no-underline w-full text-base border border-transparent
              mt-2 px-6 py-3.5 bg-${buttonColor} text-${buttonTextColor}
              hover:bg-${buttonHoverColor} active:bg-${buttonActiveColor}`}
          >
            {callToAction}
          </button>
        </form>

        {this.renderFinePrint()}
        {this.renderLogin()}
      </div>
    )
  }
}

export function PromoChecker(props: PromoCheckerProps) {
  return <PromoCheckerInner {...props} />
}
